/* eslint-disable react-refresh/only-export-components -- shared model: types + options + control together */
import { Fragment, type ComponentType, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeClosed, FilePenLine, ScanEye } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * The view states a record card can be in (PTV2-1157). A single radio (segmented in a host header,
 * hover-reveal per card) drives all of these:
 *  - sleep  → collapsed, no summary
 *  - values → collapsed, read-only summary of fields WITH values
 *  - all    → collapsed, read-only summary of ALL fields
 *  - edit   → expanded, normal edit UI
 */
export type CardViewState = 'sleep' | 'values' | 'all' | 'edit';

/** The collapsed-summary sub-mode a CollapsibleCard renders (independent of open/closed). */
export type CardSummaryView = 'off' | 'filled' | 'all';

interface CardViewOption {
  readonly value: CardViewState;
  readonly Icon: ComponentType<{ className?: string }>;
  readonly labelKey: string;
  readonly labelDefault: string;
}

export const CARD_VIEW_OPTIONS: readonly CardViewOption[] = [
  { value: 'sleep', Icon: EyeClosed, labelKey: 'patient.card.viewState.sleep', labelDefault: 'Collapsed' },
  { value: 'values', Icon: Eye, labelKey: 'patient.card.viewState.values', labelDefault: 'Collapsed — values only' },
  { value: 'all', Icon: ScanEye, labelKey: 'patient.card.viewState.all', labelDefault: 'Collapsed — all fields' },
  { value: 'edit', Icon: FilePenLine, labelKey: 'patient.card.viewState.edit', labelDefault: 'Expanded — edit' },
];

/**
 * PTV2-1157 — view states HIDDEN from the segmented (global "set all") control. The state itself stays
 * in the model (`CARD_VIEW_OPTIONS` above) so nothing downstream breaks; it's just not offered.
 */
const HIDDEN_SEGMENTED_VIEW_STATES: ReadonlySet<CardViewState> = new Set(['sleep']);

/**
 * PTV2-1157 — variants of the view control currently hidden entirely. 'hover' = the per-card control
 * (the global header control drives every card instead). The component's code is kept intact; empty
 * this set to restore per-sub-section overrides.
 */
const HIDDEN_VIEW_CONTROL_VARIANTS: ReadonlySet<'segmented' | 'hover'> = new Set(['hover']);

/** Targets to apply for a state. `summaryView` is omitted for `edit` so the prior summary is kept. */
export function viewStateToTargets(state: CardViewState): { open: boolean; summaryView?: CardSummaryView } {
  switch (state) {
    case 'edit':
      return { open: true };
    case 'values':
      return { open: false, summaryView: 'filled' };
    case 'all':
      return { open: false, summaryView: 'all' };
    default:
      return { open: false, summaryView: 'off' };
  }
}

export function openSummaryToViewState(open: boolean, summaryView: CardSummaryView): CardViewState {
  if (open) return 'edit';
  if (summaryView === 'filled') return 'values';
  if (summaryView === 'all') return 'all';
  return 'sleep';
}

/** One field/value pair for a card's collapsed read-only summary (the eye view). */
export interface CardSummaryRow {
  readonly label: ReactNode;
  readonly value: ReactNode;
  /** Whether this field has a value — controls whether it shows in the "values only" view. */
  readonly filled: boolean;
}

/**
 * Renders a card's collapsed read-only summary: a compact `label: value` list, filtered to filled
 * rows in the `values` view and all rows in the `all` view. Shared by CollapsibleCard + ClinicalSection.
 */
export function CardCollapsedSummary({
  rows,
  summaryView,
}: {
  readonly rows: readonly CardSummaryRow[];
  readonly summaryView: CardSummaryView;
}): ReactElement {
  const { t } = useTranslation();
  const visible = summaryView === 'all' ? rows : rows.filter((r) => r.filled);
  if (visible.length === 0) {
    return (
      <p className="text-xs italic text-muted-foreground">
        {t('patient.card.summaryEmpty', { defaultValue: 'No recorded values' })}
      </p>
    );
  }
  return (
    <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs">
      {visible.map((row, i) => (
        <Fragment key={i}>
          <dt className="text-muted-foreground">{row.label}</dt>
          <dd className="break-words text-foreground">{row.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

/**
 * The 4-state radio. `variant='segmented'` shows all four (a bordered control like the Sex field) —
 * used as a global "set all cards" header control. `variant='hover'` shows only the active icon and
 * slides the rest out on hover — used per card.
 */
export function CardViewStateControl({
  value,
  onChange,
  variant,
  className,
  disabled = false,
}: {
  readonly value: CardViewState;
  readonly onChange: (next: CardViewState) => void;
  readonly variant: 'segmented' | 'hover';
  readonly className?: string;
  /** Render the whole control greyed + non-interactive (e.g. while the pedigree is locked/read-only). */
  readonly disabled?: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const segmented = variant === 'segmented';
  // Some variants are hidden for now (kept in code); e.g. the per-card 'hover' control.
  if (HIDDEN_VIEW_CONTROL_VARIANTS.has(variant)) return null;
  const options = segmented
    ? CARD_VIEW_OPTIONS.filter((opt) => !HIDDEN_SEGMENTED_VIEW_STATES.has(opt.value))
    : CARD_VIEW_OPTIONS;
  return (
    <div
      className={cn(
        'inline-flex items-center',
        segmented
          ? 'divide-x divide-foreground/20 overflow-hidden rounded-md border border-foreground/25'
          : 'group gap-0.5',
        className,
      )}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        const { Icon } = opt;
        const label = t(opt.labelKey, { defaultValue: opt.labelDefault });
        return (
          <button
            key={opt.value}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={active}
            disabled={disabled}
            onClick={() => {
              onChange(opt.value);
            }}
            className={cn(
              'inline-flex h-6 shrink-0 items-center justify-center transition-all duration-150',
              disabled && 'cursor-default opacity-40',
              segmented
                ? cn('w-7', active ? 'bg-toggle-selected text-toggle-selected-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground')
                : cn(
                    'w-6 rounded-md',
                    active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                    // Hover-reveal: non-active buttons collapse to zero width until the row is hovered.
                    !active && 'w-0 overflow-hidden opacity-0 group-hover:w-6 group-hover:opacity-100',
                  ),
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}
